// This is the only file outside apiClient.ts permitted to import axios directly.
// Reason: the mount-time refresh MUST NOT go through the interceptor's
// forceLogoutAndRedirect path. A public-route visitor (password-reset link,
// invite link, verification link) would otherwise be yanked to /login before
// their deep-link could be handled. See F-17-S-4 in batch_17_security.md.
import axios from "axios";
import { create } from "zustand";
import { decodeJwtPayload } from "@/lib/jwt";

type AuthStatus = "loading" | "authenticated" | "anonymous";

type Impersonation = {
  superAdminUserId: string;
  targetUserId: string;
  targetTenantId: string;
  targetTenantSlug: string | null;
  expiresAt: number; // epoch seconds, from imp.exp
  reason: string;
};

type AuthState = {
  status: AuthStatus;
  userId: string | null;
  role: string | null;
  isSuperAdmin: boolean;
  tenantId: string | null;
  tenantSlug: string | null;
  accessToken: string | null;
  impersonation: Impersonation | null;
  // Batch 31a.2.1 — MFA pending state. In-memory only (HARD-10). The
  // token is short-lived (5 min verify / 15 min enroll) and must die
  // with the tab; persistence to localStorage / sessionStorage is
  // forbidden — see SAAS_ARCHITECTURE.md S-31a.2-3 + Sec Lead HARD-9,
  // HARD-10. ``mfaPendingExpiresAt`` is epoch ms (Date.now() + ttl_ms).
  mfaPendingToken: string | null;
  mfaPendingExpiresAt: number | null;
  mfaPendingNextStep: "enroll" | "verify" | null;
  // Email-MFA B3 — which verification method the pending challenge uses.
  // "email_code" ⇒ the challenge screen renders the email-code variant
  // (6-digit code from email + resend); "totp" (default) ⇒ authenticator
  // variant. Derived from the login response shape (presence of
  // ``code_email_sent``); in-memory only like the rest of the slot.
  mfaPendingMethod: "totp" | "email_code" | "webauthn" | null;
  // Email-MFA B3 — whether the code email actually went out on the login
  // that opened this challenge. ``false`` ⇒ the challenge surfaces a calm
  // "we couldn't email your code — resend" affordance instead of stranding
  // the user. Only meaningful when ``mfaPendingMethod === "email_code"``.
  mfaPendingCodeEmailSent: boolean | null;
  // WA-1b — the available MFA methods returned by the login response
  // (stage-1 challenge). Subset of ``["totp", "webauthn"]``. When absent
  // or empty the verify screen degrades gracefully to TOTP entry with a
  // "Use a passkey instead" secondary affordance. In-memory only.
  mfaPendingAvailableMethods: string[] | null;
  // Mutators
  hydrateOnMount: () => Promise<void>;
  setAccessToken: (token: string) => void;
  hydrateFromAccessToken: (token: string) => void;
  loginSuccess: (token: string) => void;
  mfaPendingStart: (
    token: string,
    expiresInSeconds: number,
    nextStep: "enroll" | "verify",
    options?: {
      method?: "totp" | "email_code" | "webauthn";
      codeEmailSent?: boolean;
      availableMethods?: string[];
    },
  ) => void;
  mfaPendingClear: () => void;
  reset: () => void;
};

type RefreshResponse = {
  access_token: string;
  token_type: string;
  expires_in: number;
};

// P1-LOGGED-OUT-REMEMBER-SLUG: localStorage key for last-used tenant slug.
// Persisted on login success; intentionally NOT cleared on logout so the
// /login root route can auto-redirect to /{slug}/login after a session ends.
export const LAST_SLUG_KEY = "certain.last_slug";

// Module-private TTL timer handle. Tracked outside Zustand state so
// re-renders never observe its identity; the store's mutators own the
// lifecycle (start / clear). Per HARD-9 + HARD-10 the timer must
// proactively wipe the token on TTL expiry.
let _mfaPendingTtlTimer: ReturnType<typeof setTimeout> | null = null;

export const useAuthStore = create<AuthState>((set, get) => ({
  status: "loading",
  userId: null,
  role: null,
  isSuperAdmin: false,
  tenantId: null,
  tenantSlug: null,
  accessToken: null,
  impersonation: null,
  mfaPendingToken: null,
  mfaPendingExpiresAt: null,
  mfaPendingNextStep: null,
  mfaPendingMethod: null,
  mfaPendingCodeEmailSent: null,
  mfaPendingAvailableMethods: null,

  hydrateOnMount: async () => {
    set({ status: "loading" });
    try {
      // Bare axios call — bypasses the apiClient interceptor so that a 401
      // (no refresh cookie / first-time visitor) does NOT trigger
      // forceLogoutAndRedirect. Public-route visitors stay on their deep-link.
      // See addendum F-17-S-4.
      const resp = await axios.post<RefreshResponse>(
        "/auth/refresh",
        {},
        { withCredentials: true }
      );
      get().loginSuccess(resp.data.access_token);
    } catch {
      get().mfaPendingClear();
      set({
        status: "anonymous",
        userId: null,
        role: null,
        isSuperAdmin: false,
        tenantId: null,
        tenantSlug: null,
        accessToken: null,
        impersonation: null,
      });
    }
  },

  setAccessToken: (token: string) => {
    set({ accessToken: token });
  },

  hydrateFromAccessToken: (token: string) => {
    const payload = decodeJwtPayload(token);
    if (payload === null) {
      console.warn("hydrateFromAccessToken: malformed token");
      get().mfaPendingClear();
      set({
        status: "anonymous",
        userId: null,
        role: null,
        isSuperAdmin: false,
        tenantId: null,
        tenantSlug: null,
        accessToken: null,
        impersonation: null,
      });
      return;
    }

    const userId = typeof payload["sub"] === "string" ? payload["sub"] : null;
    const role = typeof payload["role"] === "string" ? payload["role"] : null;
    const isSuperAdmin = payload["is_sa"] === true;
    const tenantId = typeof payload["tid"] === "string" ? payload["tid"] : null;
    const tenantSlug = typeof payload["tslug"] === "string" ? payload["tslug"] : null;

    // Impersonation claim hydration
    let impersonation: Impersonation | null = null;
    const imp = payload["imp"];
    if (imp && typeof imp === "object") {
      const impRec = imp as Record<string, unknown>;
      const sa = impRec["sa"];
      const uid = impRec["uid"];
      const tid = impRec["tid"];
      const exp = impRec["exp"];
      const reason = impRec["reason"];
      if (
        typeof sa === "string" &&
        typeof uid === "string" &&
        typeof tid === "string" &&
        typeof exp === "number"
      ) {
        impersonation = {
          superAdminUserId: sa,
          targetUserId: uid,
          targetTenantId: tid,
          targetTenantSlug:
            impRec["tslug"] === null ? null : typeof impRec["tslug"] === "string" ? impRec["tslug"] : null,
          expiresAt: exp,
          reason: typeof reason === "string" ? reason : "",
        };
      } else {
        console.warn("hydrateFromAccessToken: malformed imp claim");
      }
    } else {
      impersonation = null;
    }

    set({
      userId,
      role,
      isSuperAdmin,
      tenantId,
      tenantSlug,
      impersonation,
    });
  },

  loginSuccess: (token: string) => {
    // P0 passkey-v2 loop fix: COMMIT the authenticated session BEFORE
    // clearing the mfaPending slot. The old order (mfaPendingClear first)
    // nulled ``mfaPendingToken`` while the still-mounted verify page's
    // navigate-on-null-token effect was live — racing the onComplete
    // navigate and sometimes bouncing the user back to /login. The MFA
    // surface is now a modal that derives visibility from mfaPendingToken
    // and NEVER navigates on clear, but we still order the writes so the
    // store is authenticated the instant the token is cleared: any guard
    // re-render observes ``status: "authenticated"`` + a hydrated principal,
    // never a torn "cleared token, not-yet-authenticated" state.
    set({ accessToken: token, status: "authenticated" });
    // hydrateFromAccessToken resets to anonymous (accessToken nulled) if the
    // token is malformed/undecodable — preserving that behavior is why we do
    // NOT force ``status: "authenticated"`` afterward.
    get().hydrateFromAccessToken(token);
    // Only clear the pending slot AFTER the session is committed, so a
    // subscriber that fires on the token-clear already sees the new session.
    get().mfaPendingClear();

    // P1-LOGGED-OUT-REMEMBER-SLUG: persist the tenant slug so /login (root)
    // can redirect straight to /{slug}/login after logout without re-prompting.
    // Intentionally NOT cleared on reset() — survives logout by design.
    // Multi-tenant: always uses LAST slug; user can manually clear localStorage
    // (key: "certain.last_slug") to switch workspaces.
    const slug = get().tenantSlug;
    if (slug && typeof window !== "undefined") {
      window.localStorage.setItem(LAST_SLUG_KEY, slug);
    }
  },

  mfaPendingStart: (token, expiresInSeconds, nextStep, options) => {
    // HARD-9: clear any prior pending state before stashing the new
    // token. HARD-10: in-memory only — never localStorage.
    if (_mfaPendingTtlTimer !== null) {
      clearTimeout(_mfaPendingTtlTimer);
      _mfaPendingTtlTimer = null;
    }
    const expiresAt = Date.now() + expiresInSeconds * 1000;
    set({
      mfaPendingToken: token,
      mfaPendingExpiresAt: expiresAt,
      mfaPendingNextStep: nextStep,
      // Default to TOTP when the caller doesn't specify (admin/TOTP path);
      // the tenant email-code path passes method:"email_code".
      mfaPendingMethod: options?.method ?? "totp",
      mfaPendingCodeEmailSent: options?.codeEmailSent ?? null,
      mfaPendingAvailableMethods: options?.availableMethods ?? null,
    });
    // Proactive TTL expiry — wipe on the wall clock instead of
    // waiting for the next user action to discover the expired token.
    // Cap at the JS timer max (signed 32-bit ms) so absurd TTLs don't
    // overflow setTimeout into immediate firing.
    const ttlMs = Math.min(expiresInSeconds * 1000, 2_147_483_647);
    _mfaPendingTtlTimer = setTimeout(() => {
      // Re-check the slot before clearing — a concurrent
      // ``mfaPendingClear`` may have already wiped it.
      if (get().mfaPendingExpiresAt !== null) {
        get().mfaPendingClear();
      }
    }, ttlMs);
  },

  mfaPendingClear: () => {
    if (_mfaPendingTtlTimer !== null) {
      clearTimeout(_mfaPendingTtlTimer);
      _mfaPendingTtlTimer = null;
    }
    set({
      mfaPendingToken: null,
      mfaPendingExpiresAt: null,
      mfaPendingNextStep: null,
      mfaPendingMethod: null,
      mfaPendingCodeEmailSent: null,
      mfaPendingAvailableMethods: null,
    });
  },

  reset: () => {
    if (_mfaPendingTtlTimer !== null) {
      clearTimeout(_mfaPendingTtlTimer);
      _mfaPendingTtlTimer = null;
    }
    set({
      status: "anonymous",
      userId: null,
      role: null,
      isSuperAdmin: false,
      tenantId: null,
      tenantSlug: null,
      accessToken: null,
      impersonation: null,
      mfaPendingToken: null,
      mfaPendingExpiresAt: null,
      mfaPendingNextStep: null,
      mfaPendingMethod: null,
      mfaPendingCodeEmailSent: null,
      mfaPendingAvailableMethods: null,
    });
  },
}));

// Expose the auth store on ``window`` for Playwright visual fixtures
// only. Production builds do not strip this — but the only consumer is
// a Playwright fixture using ``page.evaluate``. Cost is one global key;
// benefit is deterministic visual baselines for the MFA enroll/verify
// pages, which cannot render without a pre-seeded ``mfaPendingToken``.
if (typeof window !== "undefined") {
  // Test-only window hook for Playwright visual fixtures. Visual specs
  // call ``window.__certainAuthStore.setState({...})`` to seed the
  // mfaPending slot AFTER ``hydrateOnMount`` settles; without this hook
  // the in-memory store is unreachable from the test harness. Cost is
  // one global key; benefit is deterministic baselines for the four
  // MFA pages, which cannot render without a pre-seeded
  // ``mfaPendingToken``.
  (window as unknown as { __certainAuthStore?: typeof useAuthStore }).__certainAuthStore =
    useAuthStore;
}
